import { motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { useSiteConfig } from '../../hooks/useSiteConfig';
import { openWhatsApp } from '../../utils/whatsapp';

export default function PainPointsSection() {
  const siteConfig = useSiteConfig();
  const { painPoints, contact } = siteConfig;
  const [ctaLabel, setCtaLabel] = useState(painPoints.ctaText);

  const handleCta = () => {
    setCtaLabel('⏳ 正在開啟 WhatsApp...');
    openWhatsApp(contact.whatsapp, painPoints.whatsappMessage);
  };

  return (
    <section id="pain-points" className="py-20 md:py-28 bg-deep-navy relative overflow-hidden">
      <div className="absolute inset-0 bg-circuit-grid opacity-50" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {painPoints.sectionTitle}
          </h2>
          <p className="text-lg text-neon-cyan-glow mb-3">{painPoints.subtitle}</p>
          <p className="text-sm text-gray-500 max-w-2xl mx-auto">{painPoints.intro}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {/* Problems column */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-widest mb-2 pl-1">
              商家痛點
            </h3>
            {painPoints.items.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.12 }}
                className="neon-card rounded-2xl p-6 border-neon-pink/20"
                style={{ borderColor: 'rgba(255,0,127,0.25)' }}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-neon-pink/10 flex items-center justify-center">
                    <AlertTriangle size={20} className="text-neon-pink" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-white mb-1">{item.problem}</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.problemDesc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Solutions column */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            <h3 className="text-sm font-semibold text-neon-cyan uppercase tracking-widest mb-2 pl-1">
              {painPoints.solutionTitle}
            </h3>
            {painPoints.items.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.12 }}
                className="neon-card rounded-2xl p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-neon-cyan/10 flex items-center justify-center">
                    <CheckCircle2 size={20} className="text-neon-cyan" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-white mb-1">{item.solution}</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.solutionDesc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <button
            onClick={handleCta}
            className="inline-block px-8 py-4 bg-gradient-to-r from-neon-cyan to-neon-blue text-deep-navy font-bold rounded-full transition-all duration-300 hover:brightness-110 hover:-translate-y-0.5 shadow-neon-cyan"
          >
            {ctaLabel}
          </button>
        </motion.div>
      </div>
    </section>
  );
}
